import React, { useCallback, useMemo, useState } from 'react';
import CensusMap from '../Common/CensusMap';
import Select from 'react-select';
import { useCensusFiltersContext } from '../Common/Filters/hooks/useCensusFiltersContext';
import {
    useCensusThemeSummary,
    useCensusThemeValues,
    useCensusThemeVariables
} from '../Common/hooks/useCensusTheme';
import ThemeVariablesTable from './ThemeVariablesTable';

// Painel da área selecionada: soma de todas as variáveis do tema nos setores da área.
function ThemePanel({ theme, cityId, year, selection }) {
    const sectorIds = selection.isCity ? null : selection.tracts.map(tract => tract.id);
    const summary = useCensusThemeSummary(theme, cityId, year, sectorIds);

    return (
        <ThemeVariablesTable
            summary={summary.data}
            isLoading={summary.isPending}
            isUpdating={summary.isPlaceholderData}
            isError={summary.isError}
            error={summary.error}
        />
    );
}

/**
 * Página genérica de um tema do Censo por setor censitário: o mapa é colorido pela variável
 * escolhida e o painel lista todas as variáveis do tema na área clicada.
 */
const ThemeMap = ({ theme }) => {
    const { filters } = useCensusFiltersContext();
    const cityId = filters.cidade?.value;
    const year = filters.year?.value;

    const { data: variables = [] } = useCensusThemeVariables(theme);
    const options = useMemo(
        () => variables.map(({ code, description }) => ({ value: code, label: description })),
        [variables]
    );
    const [chosen, setChosen] = useState(null);
    const variable = options.find(option => option.value === chosen?.value) || options[0] || null;

    const { data: values, isPending: areValuesPending } = useCensusThemeValues(theme, cityId, year, variable?.value);
    const valueBySector = useMemo(
        () => Object.fromEntries((values?.values || []).map(({ id, value }) => [id, value])),
        [values]
    );
    const valueOf = useCallback(tract => (tract.id in valueBySector ? valueBySector[tract.id] : null), [
        valueBySector
    ]);

    return (
        <CensusMap
            cityId={cityId}
            year={year}
            valueOf={valueOf}
            metricLabel=""
            // Segura o skeleton até as variáveis e os valores da primeira chegarem: sem isso o mapa
            // aparecia todo cinza e depois colorido.
            isLoading={!options.length || areValuesPending}
            toolbar={
                <>
                    <label className="censo-toolbar-label" htmlFor={`censo-variable-${theme}`}>
                        Colorir mapa por
                    </label>
                    <Select
                        inputId={`censo-variable-${theme}`}
                        classNamePrefix="select"
                        options={options}
                        value={variable}
                        onChange={setChosen}
                        isLoading={!options.length}
                        placeholder="Digite para buscar uma variável"
                        noOptionsMessage={() => 'Nenhuma variável encontrada'}
                        // Menu num portal: abre por cima do mapa (Leaflet usa z-index até 1000).
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 1100 }) }}
                    />
                </>
            }
            renderPanel={selection => (
                <ThemePanel theme={theme} cityId={cityId} year={year} selection={selection} />
            )}
        />
    );
};

export default ThemeMap;
